import { common, enums } from "@shared/constants";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import { PwycPriceManager } from "@shared/modules/seating/services/PwycPriceManager";
import { showTicketButtons } from "@shared/modules/events/utils/detail-page-helpers";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import { isOnSaleTicketType } from "@shared/modules/ticket-types/utils/ticket-type-utils";
import { useCallback, useMemo, useState } from "react";
import { DefaultSeatSelectProps } from "./DefaultSeatSelect.props";

const pwycPriceManager = PwycPriceManager.getInstance();

export function useDefaultSeatSelect(props: DefaultSeatSelectProps) {
  const {
    item,
    voucherData,
    ticketAuthArray,
    updateTicketAuthArray,
    selectItemPermission,
  } = props;

  const { seatingServiceState, seatingEventData } = useSeatingState();
  const [pwycPrice, setPwycPrice] = useState<string | null>(null);
  const purchaseLimits = seatingServiceState.overridePurchaseLimits;
  const { mapItemType } = seatingServiceState;

  const { isBasketUpdating, purchaseSourcePlatform, paymentType, itemGroups } =
    useBasketService();

  const isGroupSale = useMemo(
    () => seatingServiceState.isGroupSalesMap(),
    [seatingServiceState]
  );
  const isDynamicHold = useMemo(
    () => Boolean(seatingServiceState.basketHoldID),
    [seatingServiceState]
  );

  // allow sold out items to be selected on group sales maps
  const isSoldOut = useMemo(
    () =>
      (item?.usageRemaining === 0 || item?.sold_out) &&
      !isGroupSale &&
      !isDynamicHold,
    [item, isGroupSale, isDynamicHold]
  );

  const showButtons = useMemo(
    () =>
      (showTicketButtons(
        item,
        seatingEventData,
        voucherData.userVouchers,
        ticketAuthArray,
        updateTicketAuthArray,
        voucherData.basketVoucherCode,
        isDynamicHold
      ) ||
        isGroupSale ||
        isDynamicHold ||
        mapItemType === common.SeatingMapItemType.MEMBERSHIP) &&
      !isSoldOut,
    [
      item,
      seatingEventData,
      voucherData.userVouchers,
      ticketAuthArray,
      updateTicketAuthArray,
      voucherData.basketVoucherCode,
      isDynamicHold,
      isGroupSale,
      mapItemType,
      isSoldOut,
    ]
  );

  const isDisabledSelector = useMemo(
    () =>
      isBasketUpdating ||
      (!isOnSaleTicketType(item) &&
        mapItemType !== common.SeatingMapItemType.MEMBERSHIP),
    [isBasketUpdating, isOnSaleTicketType, item, mapItemType]
  );

  const handleSetPwycPrice = useCallback(
    (value: string) => {
      const itemType =
        mapItemType === common.SeatingMapItemType.MEMBERSHIP
          ? enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id
          : enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id;
      pwycPriceManager.setPwycPrice(item.id, value, itemType);
      setPwycPrice(value);
    },
    [item.id, mapItemType]
  );

  const getMinPurchaseLimit = useCallback(() => {
    if (purchaseLimits) {
      return purchaseLimits.min;
    }
    if (item.minimum_purchase_limit) {
      return item.minimum_purchase_limit;
    }
    // 1 is the default minimum purchase limit for items
    return item.$countOptions?.[1] ?? common.DEFAULT_MIN_TICKET_PURCHASE_LIMIT;
  }, [purchaseLimits, item]);

  const pwycConfig = item?.pay_what_you_can_configuration;

  const currentPwycPrice = itemGroups?.find(
    (ig) => ig.type === item.id && Boolean(ig.pay_what_you_can_price)
  )?.pay_what_you_can_price;

  // Get the default PWYC price: use item's base price as the default
  // This matches the defaultPrice prop passed to PWYCTicketType in the component
  const defaultPwycPrice = item.price?.toString();

  const selectItemCallback = () =>
    selectItemPermission({
      itemTypeId: item.id,
      permissionId: item.permissionId,
      minPurchaseLimit: getMinPurchaseLimit(),
      // Use the selected price, or existing basket price, or fall back to the default
      pwycPrice: pwycPrice ?? currentPwycPrice ?? defaultPwycPrice,
    });

  return {
    selectItemCallback,
    purchaseLimits,
    showButtons,
    isDisabledSelector,
    isSoldOut,
    purchaseSourcePlatform,
    paymentType,
    pwycPrice,
    handleSetPwycPrice,
    currentPwycPrice,
    pwycConfig,
  };
}
